import type { CircleFormat } from '../elements/shapes/circle/circle.model';
import type { SolvedCircle } from './solver.model';

/**
 * Auto-detect circle format from available keys.
 */
export function detectCircleFormat(values: Record<string, unknown>): CircleFormat {
  if (values['r'] !== undefined) {
    return 'CR';
  }
  if (values['diameterA'] !== undefined && values['diameterB'] !== undefined) {
    return 'DP';
  }

  throw new Error(
    `Cannot detect circle format: need r (+ optional center) or diameterA + diameterB. Got: ${Object.keys(values).join(
      ', ',
    )}`,
  );
}

/**
 * Solve a circle given numeric values. Format is auto-detected if not provided.
 *
 * Formats:
 * - CR: radius r (+ optional center coordinates)
 * - DP: two diameter points -> center = midpoint, radius = half distance
 */
export function solveCircle(values: Record<string, number | [number, number]>, format?: CircleFormat): SolvedCircle {
  const resolvedFormat = format ?? detectCircleFormat(values);

  let center: [number, number];
  let radius: number;

  switch (resolvedFormat) {
    case 'CR': {
      radius = values['r'] as number;
      if (radius === undefined) {
        throw new Error('CR format requires r');
      }
      if (radius <= 0) {
        throw new Error(`Radius must be positive: r=${radius}`);
      }
      // Center defaults to origin if not provided
      const cx = (values['cx'] as number) ?? 0;
      const cy = (values['cy'] as number) ?? 0;
      center = [cx, cy];
      break;
    }

    case 'DP': {
      const a = values['diameterA'] as [number, number];
      const b = values['diameterB'] as [number, number];
      if (!a || !b) {
        throw new Error('DP format requires diameterA and diameterB');
      }
      center = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      radius = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2) / 2;
      if (radius <= 0) {
        throw new Error('Diameter points must be distinct');
      }
      break;
    }

    default:
      throw new Error(`Unknown circle format: ${resolvedFormat as string}`);
  }

  return {
    center,
    radius,
    area: Math.PI * radius * radius,
    circumference: 2 * Math.PI * radius,
  };
}
